import React, { useEffect, useState, useCallback } from 'react';
import { Card, CardContent } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../../components/ui/dialog';
import { Loader2, Flame, Heart, AlertTriangle, Snowflake, RefreshCw, TrendingUp, Info } from 'lucide-react';
import { toast } from 'sonner';
import { useNavigate } from 'react-router-dom';
import { EngagementBadge, EngagementBreakdown } from './EngagementBadge';

const BUCKETS = [
  { key: 'champion', label: 'Champions', color: 'bg-emerald-500', ring: 'ring-emerald-500', text: 'text-emerald-700', bg: 'bg-emerald-50', icon: Flame, hint: 'Score 80–100 · give recently, often, and growing' },
  { key: 'engaged',  label: 'Engaged',   color: 'bg-blue-500',    ring: 'ring-blue-500',    text: 'text-blue-700',    bg: 'bg-blue-50',    icon: Heart, hint: 'Score 60–79 · steady givers you are cultivating' },
  { key: 'at_risk',  label: 'At risk',   color: 'bg-amber-500',   ring: 'ring-amber-500',   text: 'text-amber-700',   bg: 'bg-amber-50',   icon: AlertTriangle, hint: 'Score 30–59 · slipping — reach out soon' },
  { key: 'cold',     label: 'Cold',      color: 'bg-gray-400',    ring: 'ring-gray-400',    text: 'text-gray-700',    bg: 'bg-gray-50',    icon: Snowflake, hint: 'Score 0–29 · silent or brand new — needs a fresh touchpoint' },
];

const fmt$ = (n) => `$${(n || 0).toLocaleString('en-US', { minimumFractionDigits: 0, maximumFractionDigits: 0 })}`;

export function EngagementTab({ api }) {
  const navigate = useNavigate();
  const [donors, setDonors] = useState([]);
  const [loading, setLoading] = useState(true);
  const [recomputing, setRecomputing] = useState(false);
  const [bucketFilter, setBucketFilter] = useState(null);
  const [q, setQ] = useState('');
  const [distribution, setDistribution] = useState({ champion: 0, engaged: 0, at_risk: 0, cold: 0 });
  const [detailDonor, setDetailDonor] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get('/donors?sort_by=engagement_score&sort_dir=desc');
      setDonors(data);
      const dist = { champion: 0, engaged: 0, at_risk: 0, cold: 0 };
      for (const d of data) {
        const b = d.engagement_bucket;
        if (b && dist[b] !== undefined) dist[b] += 1;
      }
      setDistribution(dist);
    } catch (e) {
      toast.error('Failed to load engagement data');
    } finally {
      setLoading(false);
    }
  }, [api]);

  useEffect(() => { load(); }, [load]);

  const recomputeAll = async () => {
    setRecomputing(true);
    const t = toast.loading('Recomputing every donor score…');
    try {
      const { data } = await api.post('/donors-recompute-engagement-all');
      toast.dismiss(t);
      toast.success(`Scored ${data.scored} donors`);
      await load();
    } catch (e) {
      toast.dismiss(t);
      toast.error('Recompute failed');
    } finally {
      setRecomputing(false);
    }
  };

  const openDetail = async (d) => {
    setDetailDonor(d);
    setDetailLoading(true);
    try {
      // Trigger fresh compute + fetch enriched record with engagement_detail
      const r = await api.post(`/donors/${d.id}/recompute-engagement`);
      setDetailDonor(prev => prev ? { ...prev, ...r.data } : null);
    } catch {
      // ignore — the existing donor summary will still render
    } finally {
      setDetailLoading(false);
    }
  };

  const total = donors.length;
  const filtered = donors.filter(d => {
    if (bucketFilter && d.engagement_bucket !== bucketFilter) return false;
    if (q) {
      const query = q.toLowerCase();
      const name = (d.name || '').toLowerCase();
      const email = (d.email || '').toLowerCase();
      if (!name.includes(query) && !email.includes(query)) return false;
    }
    return true;
  });
  const hasScores = donors.some(d => d.engagement_bucket);

  if (loading) {
    return <div className="flex items-center justify-center py-20"><Loader2 className="h-8 w-8 animate-spin text-[#6B3FA0]" /></div>;
  }

  return (
    <div className="space-y-4" data-testid="engagement-tab">
      {/* Header + action bar */}
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h2 className="text-2xl font-semibold flex items-center gap-2">
            <TrendingUp className="h-5 w-5 text-[#6B3FA0]" /> Engagement scores
          </h2>
          <p className="text-sm text-gray-500 mt-0.5">
            0–100 score for every donor · Recency (30) + Frequency (25) + Trend (25) + Touchpoints (20)
          </p>
        </div>
        <Button onClick={recomputeAll} disabled={recomputing}
                className="bg-[#6B3FA0] hover:bg-[#5A3488] text-white"
                data-testid="recompute-all-btn">
          {recomputing ? <><Loader2 className="h-4 w-4 mr-1 animate-spin" /> Recomputing…</>
                       : <><RefreshCw className="h-4 w-4 mr-1" /> Recompute all scores</>}
        </Button>
      </div>

      {!hasScores && (
        <Card className="border-amber-200 bg-amber-50">
          <CardContent className="p-4 flex items-start gap-3">
            <Info className="h-5 w-5 text-amber-600 shrink-0 mt-0.5" />
            <div className="text-sm">
              <div className="font-semibold text-amber-900">Scores not computed yet</div>
              <div className="text-amber-800">Click <em>Recompute all scores</em> above to score every donor at once. New gifts also refresh scores automatically.</div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Distribution cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3" data-testid="bucket-distribution">
        {BUCKETS.map(b => {
          const count = distribution[b.key] || 0;
          const pct = total > 0 ? Math.round((count / total) * 100) : 0;
          const active = bucketFilter === b.key;
          const Icon = b.icon;
          return (
            <button key={b.key}
                    onClick={() => setBucketFilter(active ? null : b.key)}
                    className={`text-left rounded-xl border-2 p-4 transition-all ${active ? `border-transparent ${b.bg} ring-2 ${b.ring}` : 'border-gray-200 hover:border-gray-300 bg-white'}`}
                    data-testid={`bucket-card-${b.key}`}>
              <div className="flex items-center justify-between mb-2">
                <div className={`h-10 w-10 rounded-full ${b.color} flex items-center justify-center text-white`}>
                  <Icon className="h-5 w-5" />
                </div>
                <div className="text-3xl font-bold tabular-nums">{count}</div>
              </div>
              <div className={`text-sm font-semibold ${b.text}`}>{b.label}</div>
              <div className="text-xs text-gray-500 mt-1">{pct}% of scored donors</div>
              {/* mini bar */}
              <div className="h-1.5 mt-2 bg-gray-100 rounded-full overflow-hidden">
                <div className={`h-full ${b.color}`} style={{ width: `${pct}%` }} />
              </div>
            </button>
          );
        })}
      </div>

      {/* Filter row */}
      <div className="flex items-center gap-2 flex-wrap">
        <Input placeholder="Search this list…" value={q} onChange={e => setQ(e.target.value)}
               className="max-w-xs" data-testid="engagement-search" />
        {bucketFilter && (
          <button onClick={() => setBucketFilter(null)}
                  className="text-xs text-[#6B3FA0] hover:underline"
                  data-testid="engagement-clear-filter">
            Clear bucket filter
          </button>
        )}
        <div className="ml-auto text-xs text-gray-500">
          Showing {filtered.length} of {total} donor{total === 1 ? '' : 's'}
        </div>
      </div>

      {/* Ranked donor list */}
      {filtered.length === 0 ? (
        <Card><CardContent className="p-10 text-center text-gray-500">
          No donors match this filter.
        </CardContent></Card>
      ) : (
        <div className="grid gap-2" data-testid="engagement-list">
          {filtered.map((d, i) => {
            const meta = BUCKETS.find(b => b.key === d.engagement_bucket);
            const initials = ((d.first_name || d.name || '?')[0] + (d.last_name?.[0] || '')).toUpperCase();
            return (
              <Card key={d.id}
                    className="hover:shadow-md transition-shadow cursor-pointer"
                    onClick={() => openDetail(d)}
                    data-testid={`engagement-row-${d.id}`}>
                <CardContent className="p-3 flex items-center gap-3">
                  <div className="text-xs text-gray-400 tabular-nums w-8 text-right shrink-0">#{i + 1}</div>
                  <div className={`h-10 w-10 rounded-full ${meta?.color || 'bg-gray-300'} text-white flex items-center justify-center font-semibold text-sm shrink-0`}>
                    {initials}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="font-medium truncate">{d.name || 'Unnamed'}</span>
                      {d.engagement_bucket && <EngagementBadge score={d.engagement_score} bucket={d.engagement_bucket} />}
                    </div>
                    <div className="text-xs text-gray-500 truncate mt-0.5">
                      {d.email || '—'}
                      {d.last_gift_date && ` · last gift ${d.last_gift_date}`}
                      {d.gift_count > 0 && ` · ${d.gift_count} gift${d.gift_count === 1 ? '' : 's'}`}
                    </div>
                  </div>
                  <div className="text-right shrink-0">
                    <div className="text-2xl font-bold tabular-nums" style={{ color: meta?.color?.replace('bg-', '') ? undefined : '#6B3FA0' }}>
                      {d.engagement_score != null ? d.engagement_score : '—'}
                    </div>
                    <div className="text-[10px] text-gray-500 uppercase">score</div>
                  </div>
                  <button className="text-xs text-[#6B3FA0] hover:underline shrink-0 ml-1"
                          onClick={(e) => { e.stopPropagation(); navigate(`/donors/${d.id}`); }}
                          data-testid={`engagement-open-profile-${d.id}`}>
                    Profile →
                  </button>
                </CardContent>
              </Card>
            );
          })}
        </div>
      )}

      {/* Detail dialog */}
      <Dialog open={!!detailDonor} onOpenChange={o => !o && setDetailDonor(null)}>
        <DialogContent className="max-w-lg" data-testid="engagement-detail-dialog">
          {detailDonor && (
            <>
              <DialogHeader>
                <DialogTitle className="flex items-center gap-2 flex-wrap">
                  {detailDonor.name}
                  {detailDonor.engagement_bucket && <EngagementBadge score={detailDonor.engagement_score} bucket={detailDonor.engagement_bucket} size="lg" showScore />}
                </DialogTitle>
              </DialogHeader>
              {detailLoading && !detailDonor.engagement_detail ? (
                <div className="py-8 flex justify-center"><Loader2 className="h-6 w-6 animate-spin text-[#6B3FA0]" /></div>
              ) : (
                <div className="space-y-3 py-2">
                  <EngagementBreakdown detail={detailDonor.engagement_detail} />
                  <div className="text-xs text-gray-500 border-t pt-2">
                    Total given: <strong>{fmt$(detailDonor.total_given)}</strong> ·
                    Gifts: <strong>{detailDonor.gift_count || 0}</strong>
                    {detailDonor.last_gift_date && <> · Last gift: <strong>{detailDonor.last_gift_date}</strong></>}
                  </div>
                  <div className="flex justify-end gap-2">
                    <Button variant="outline" onClick={() => setDetailDonor(null)}>Close</Button>
                    <Button onClick={() => { const id = detailDonor.id; setDetailDonor(null); navigate(`/donors/${id}`); }}
                            data-testid="engagement-goto-profile">
                      Open profile →
                    </Button>
                  </div>
                </div>
              )}
            </>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
